import { type SupabaseClient, createClient } from '@supabase/supabase-js';
import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useAuth } from '../lib/auth/hooks';
import {
  type ZooConfig,
  resolveZooByEmail,
  saveZooConfig,
} from '../lib/auth/onboarding';
import {
  discoverWithPasskey,
  getPasskeyLabel,
  isPlatformAuthenticatorAvailable,
  isWebAuthnSupported,
  startConditionalPasskey,
  startPasskeyLogin,
} from '../lib/auth/webauthn';
import { getStoredSecurityPolicy } from '../lib/auth/security-policy';
import { getSupabase } from '../lib/supabase/client';

// ─── Platform API ───

const isStaging = process.env.SUPABASE_FORCE === 'true';

const PLATFORM_URL = isStaging
  ? process.env.PLATFORM_SUPABASE_URL_STAGING || ''
  : process.env.PLATFORM_SUPABASE_URL || '';

const PLATFORM_ANON_KEY = isStaging
  ? process.env.PLATFORM_SUPABASE_ANON_KEY_STAGING || ''
  : process.env.PLATFORM_SUPABASE_ANON_KEY || '';

function platformHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${PLATFORM_ANON_KEY}`,
    apikey: PLATFORM_ANON_KEY,
  };
}

// ─── Types ───

/**
 * Auth page — progressive disclosure, single card.
 *
 * The card smoothly expands when zoo is resolved:
 *   !resolved → identifier input + biometric icon + QR
 *    resolved → pill + password (card grows, no page swap)
 *
 * Sub-views (forgot, magic-sent, qr) replace the card content.
 */
type SubView = 'none' | 'forgot' | 'magic-sent' | 'qr';

interface StoredIdentity {
  email: string;
  name: string;
  avatarUrl: string | null;
  zooConfig: ZooConfig;
  lastLoginAt: number;
}

// ─── Identity Persistence ───

const IDENTITIES_KEY = 'zbk3_identities';

function getStoredIdentities(): StoredIdentity[] {
  try {
    const raw = localStorage.getItem(IDENTITIES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as StoredIdentity[];
  } catch {
    return [];
  }
}

function saveIdentity(identity: StoredIdentity): void {
  const identities = getStoredIdentities().filter((i) => i.email !== identity.email);
  identities.unshift(identity);
  localStorage.setItem(IDENTITIES_KEY, JSON.stringify(identities.slice(0, 8)));
}

function removeIdentity(email: string): StoredIdentity[] {
  const identities = getStoredIdentities().filter((i) => i.email !== email);
  localStorage.setItem(IDENTITIES_KEY, JSON.stringify(identities));
  return identities;
}

function removeAllIdentities(): void {
  localStorage.removeItem(IDENTITIES_KEY);
}

// ─── Identifier Resolve ───

/** Detect format and resolve via appropriate path. */
async function resolveIdentifier(identifier: string): Promise<ZooConfig & { resolved_email?: string }> {
  if (identifier.includes('@')) {
    return resolveZooByEmail(identifier);
  }
  // Employee ID path — call Platform resolve with ?identifier=
  const res = await fetch(
    `${PLATFORM_URL}/functions/v1/resolve?identifier=${encodeURIComponent(identifier)}`,
    { method: 'GET', headers: platformHeaders() },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).error ?? 'No account found for this ID');
  }
  return res.json();
}

// ─── Shared Styles (BEP 2026 enterprise) ───

const inputStyle: React.CSSProperties = {
  width: '100%',
  paddingTop: 13,
  paddingBottom: 13,
  paddingLeft: 16,
  paddingRight: 16,
  borderRadius: 12,
  border: '1.5px solid var(--color-border-opaque)',
  backgroundColor: 'var(--color-bg-primary)',
  fontSize: 16,
  lineHeight: '24px',
  color: 'var(--color-label-primary)',
  fontFamily: 'var(--font-family-primary)',
  outline: 'none',
  boxSizing: 'border-box' as const,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  minHeight: 50,
  WebkitAppearance: 'none' as const,
};

const primaryBtnStyle: React.CSSProperties = {
  width: '100%',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 20,
  paddingRight: 20,
  borderRadius: 12,
  border: 'none',
  backgroundColor: 'var(--color-tint-blue)',
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
  fontFamily: 'var(--font-family-primary)',
  minHeight: 50,
  transition: 'opacity 0.15s ease, transform 0.1s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  cursor: 'pointer',
  letterSpacing: '-0.01em',
};

const outlineBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle,
  backgroundColor: 'transparent',
  color: 'var(--color-label-primary)',
  border: '1.5px solid var(--color-border-opaque)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 600,
  color: 'var(--color-label-secondary)',
  marginBottom: 6,
};

const textBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-tint-blue)',
  fontSize: 14,
  fontFamily: 'var(--font-family-primary)',
  fontWeight: 500,
  cursor: 'pointer',
  padding: '4px 8px',
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 8,
  transition: 'background-color 0.1s ease',
};

const pillStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  paddingTop: 12,
  paddingBottom: 12,
  paddingLeft: 16,
  paddingRight: 16,
  borderRadius: 12,
  backgroundColor: 'var(--color-fill-quaternary)',
  gap: 12,
};

function focusBorder(e: React.FocusEvent<HTMLInputElement>) {
  e.currentTarget.style.borderColor = 'var(--color-tint-blue)';
  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(15, 127, 255, 0.12)';
}
function blurBorder(e: React.FocusEvent<HTMLInputElement>) {
  e.currentTarget.style.borderColor = 'var(--color-border-opaque)';
  e.currentTarget.style.boxShadow = 'none';
}

function dis(isDisabled: boolean): React.CSSProperties {
  return { opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' };
}

// ─── SVG Icons ───

const LOGO_PATH =
  'M7.38-3.11L5.86-0.76L3.76-0.81C2.65-0.83 2.80 0.75 3.80 0.64C4.41 0.60 4.78 1.17 4.41 1.59C3.71 2.33 5.10 3.42 5.64 2.44C5.93 1.91 6.60 1.98 6.73 2.54C7.01 3.82 8.52 3.26 8.18 2.30L7.37 0.08L8.65-1.15L11.34 0.70C13.50 4.35 17.68 6.62 22.96 6.62C31.37 6.62 35.53-0.97 35.53-5.10C35.53-6.56 33.74-6.61 33.57-5.25C32.98-0.26 29.03 3.25 24.66 3.25C22.01 3.25 19.68 2.13 18.19 0.36L19.62-1.98L21.29-1.37L21.45 0.98C21.53 1.97 23.14 1.84 22.88 0.62C22.76 0.06 23.36-0.28 23.82 0.08C24.69 0.73 25.49-0.81 24.59-1.20C24.06-1.43 24.13-2.25 24.74-2.30C25.75-2.33 25.18-4.11 24.21-3.64L22.31-2.75L19.97-4.28C19.29-4.71 18.46-4.61 17.96-3.95L16.73-2.29C16.54-2.96 16.43-3.68 16.43-4.42C16.43-6.62 17.21-8.87 18.56-10.51L18.57-9.02C18.57-8.20 19.21-7.63 20.00-7.59L22.79-7.43L23.81-5.60C24.33-4.65 25.72-5.64 25.02-6.40C24.62-6.84 25.01-7.48 25.56-7.36C26.52-7.16 26.75-8.86 25.68-8.85C25.09-8.85 24.81-9.46 25.23-9.84C26.17-10.69 24.94-11.71 24.29-10.97L22.77-9.16L21.07-9.66L21.30-12.60C21.98-12.88 22.73-13.03 23.53-13.03C24.79-13.03 24.77-12.26 26.25-12.26C28.30-12.26 32.78-15.39 32.78-17.68C32.78-19.18 30.46-20.10 26.72-20.10C24.05-20.10 23.09-19.43 22.91-18.19C22.77-17.27 22.28-16.78 21.38-16.78C20.72-16.78 20.09-16.73 19.46-16.63L16.54-18.01L16.96-19.76L19.29-20.17C20.25-20.34 19.97-21.93 18.79-21.55C18.23-21.38 17.84-21.93 18.14-22.42C18.68-23.34 17.11-23.99 16.80-23.06C16.62-22.52 16.01-22.44 15.74-23.03C15.27-24.01 13.84-23.36 14.40-22.42L15.47-20.64L14.21-18.14C13.85-17.43 14.04-16.59 14.74-16.18L15.94-15.48C12.25-13.59 9.74-9.83 9.74-5.38C9.74-4.66 9.80-3.97 9.90-3.30L9.35-3.62C8.63-4.03 7.82-3.79 7.38-3.11Z';
const LOGO_TRANSFORM =
  'matrix(0.6145426607213589, 0, 0, 0.6145426607213589, 0.16453108624421442, 17.191410939172975)';

const ZoobookLogo = ({ size = 72 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="var(--color-tint-blue)" style={{ marginBottom: 12, transform: 'translateX(2px)' }} aria-hidden="true">
    <path transform={LOGO_TRANSFORM} d={LOGO_PATH} />
  </svg>
);

const BiometricIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z" />
    <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
  </svg>
);

const QrIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function getInitials(name: string): string {
  return name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

// ─── CSS Animations (injected once) ───

const AUTH_ANIMATIONS_CSS = `
@keyframes auth-slide-down {
  from { opacity: 0; transform: translateY(-8px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 500px; }
}
@keyframes auth-slide-up {
  from { opacity: 1; transform: translateY(0);    max-height: 500px; }
  to   { opacity: 0; transform: translateY(-8px); max-height: 0; }
}
@keyframes auth-fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes auth-shake {
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-6px); }
  40%      { transform: translateX(6px); }
  60%      { transform: translateX(-4px); }
  80%      { transform: translateX(4px); }
}
.auth-expand-enter {
  animation: auth-slide-down 0.3s ease forwards;
  overflow: hidden;
}
.auth-expand-exit {
  animation: auth-slide-up 0.25s ease forwards;
  overflow: hidden;
}
.auth-fade-enter {
  animation: auth-fade-in 0.25s ease forwards;
}
.auth-error-enter {
  animation: auth-fade-in 0.3s ease forwards, auth-shake 0.4s ease 0.1s;
}
@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 1; }
}
`;

/** Expand/collapse helper — uses CSS classes for animation */
function expandStyle(visible: boolean): React.CSSProperties {
  return {
    maxHeight: visible ? 500 : 0,
    opacity: visible ? 1 : 0,
    overflow: 'hidden',
    transition: 'max-height 0.3s ease, opacity 0.25s ease',
  };
}

// ─── QR Login View ───

const QR_POLL_INTERVAL = 3000;

function QRLoginView({
  onSuccess,
  onBack,
}: {
  onSuccess: (result: { access_token: string; refresh_token: string; zoo_config: ZooConfig }) => void;
  onBack: () => void;
}) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(120);
  const [qrError, setQrError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const initQrSession = useCallback(async () => {
    stopPolling();
    setQrError(null);
    setQrDataUrl(null);
    setCountdown(120);
    try {
      const res = await fetch(`${PLATFORM_URL}/functions/v1/qr-login-init`, {
        method: 'POST', headers: platformHeaders(), body: '{}',
      });
      if (!res.ok) throw new Error('Failed to create QR session');
      const { session_id, token, ttl_seconds } = await res.json();
      setCountdown(ttl_seconds);
      const qrPayload = `zoobook://qr-login/${session_id}:${token}`;
      setQrDataUrl(await QRCode.toDataURL(qrPayload, { width: 200, margin: 2, color: { dark: '#000000', light: '#ffffff' } }));

      countdownRef.current = setInterval(() => {
        setCountdown((c) => { if (c <= 1) { initQrSession(); return 0; } return c - 1; });
      }, 1000);

      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch(`${PLATFORM_URL}/functions/v1/qr-login-status`, {
            method: 'POST', headers: platformHeaders(), body: JSON.stringify({ session_id }),
          });
          if (!sr.ok) return;
          const sd = await sr.json();
          if (sd.status === 'approved') { stopPolling(); onSuccess(sd); }
          else if (sd.status === 'expired') stopPolling();
        } catch { /* retry next poll */ }
      }, QR_POLL_INTERVAL);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : 'Failed to generate QR code');
    }
  }, [onSuccess, stopPolling]);

  useEffect(() => { initQrSession(); return stopPolling; }, [initQrSession, stopPolling]);

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <ZoobookLogo size={64} />
        <h1 style={{ fontSize: 'var(--font-size-title3)', lineHeight: 'var(--line-height-title3)', fontWeight: 700, color: 'var(--color-label-primary)', margin: 0 }}>
          Sign in with your phone
        </h1>
      </div>
      {qrError && <div role="alert" style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: 'var(--color-tint-light-red)', color: 'var(--color-tint-red)', fontSize: 'var(--font-size-footnote)', marginBottom: 16, textAlign: 'center' }}>{qrError}</div>}
      <div style={{ width: 200, height: 200, margin: '0 auto 20px', borderRadius: 12, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-fill-quaternary)' }}>
        {qrDataUrl ? <img src={qrDataUrl} alt="QR code for mobile login" width={200} height={200} /> : <div style={{ color: 'var(--color-label-tertiary)', fontSize: 'var(--font-size-footnote)' }}>Generating...</div>}
      </div>
      <p style={{ fontSize: 'var(--font-size-body)', color: 'var(--color-label-secondary)', textAlign: 'center', margin: '0 0 8px 0' }}>Open Zoobook on your phone and scan this code to sign in.</p>
      <p style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)', textAlign: 'center', margin: '0 0 20px 0' }}>QR code expires in {minutes}:{seconds.toString().padStart(2, '0')}</p>
      <div style={{ paddingTop: 16, borderTop: '1px solid var(--color-border-opaque)', textAlign: 'center' }}>
        <button type="button" onClick={() => { stopPolling(); onBack(); }} style={{ ...textBtnStyle, justifyContent: 'center', width: '100%', color: 'var(--color-label-tertiary)' }}>← Back to sign in</button>
      </div>
    </>
  );
}

// ─── Main Component ───

export function AuthPage() {
  const identities = getStoredIdentities();

  // Core state: is the zoo resolved? Controls the expand animation.
  const [resolved, setResolved] = useState(identities.length >= 1);
  const [showAccountChooser, setShowAccountChooser] = useState(identities.length >= 2);
  const [subView, setSubView] = useState<SubView>('none');

  // Form
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [resolvedConfig, setResolvedConfig] = useState<ZooConfig | null>(
    identities.length === 1 ? identities[0].zooConfig : null,
  );
  const [resolvedEmail, setResolvedEmail] = useState<string | null>(
    identities.length === 1 ? identities[0].email : null,
  );

  // UI
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Refs
  const identifierRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const tempClientRef = useRef<SupabaseClient | null>(null);
  const conditionalAbortRef = useRef<AbortController | null>(null);

  // Hooks
  const { signIn, signInOffline, sendMagicLink, resetPassword, signOut, session, refreshZooConfig } = useAuth();
  const { zooConfig, hasZooConfig } = useAuth();
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const policy = getStoredSecurityPolicy();

  // Staging zoo name
  const [realZooName, setRealZooName] = useState<string | null>(null);
  const [zooNameLoading, setZooNameLoading] = useState(isStaging && resolved);

  // ─── Init ───

  useEffect(() => { isPlatformAuthenticatorAvailable().then(setPasskeyAvailable); }, []);

  useEffect(() => { if (session) navigate('/', { replace: true }); }, [session, navigate]);

  // Auto-trigger biometric for returning single user
  useEffect(() => {
    if (resolved && identities.length === 1 && passkeyAvailable && isOnline && resolvedConfig) {
      handlePasskeyLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, passkeyAvailable, isOnline]);

  // Conditional mediation
  useEffect(() => {
    if (resolved || !isWebAuthnSupported()) return;
    const abort = new AbortController();
    conditionalAbortRef.current = abort;
    startConditionalPasskey(abort)
      .then((r) => { if (r && !abort.signal.aborted) handlePasskeyDiscoverSuccess(r); })
      .catch(() => {});
    return () => { abort.abort(); conditionalAbortRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved]);

  // Resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Staging zoo name fetch
  useEffect(() => {
    if (!isStaging || !resolved) return;
    const client = getSupabase();
    if (!client) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const fetch_ = () => {
      client.rpc('get_zoo_name').then(
        ({ data }) => { if (typeof data === 'string' && data) setRealZooName(data); else timer = setTimeout(fetch_, 10_000); setZooNameLoading(false); },
        () => { setZooNameLoading(false); timer = setTimeout(fetch_, 10_000); },
      );
    };
    fetch_();
    return () => { if (timer) clearTimeout(timer); };
  }, [resolved]);

  if (session) return null;

  // ─── Helpers ───

  function getTempClient(config: ZooConfig): SupabaseClient {
    if (!tempClientRef.current) tempClientRef.current = createClient(config.supabaseUrl, config.supabaseAnonKey);
    return tempClientRef.current;
  }

  function clearMessages() { setError(null); setSuccess(null); }

  function collapse() {
    setResolved(false);
    setShowAccountChooser(getStoredIdentities().length >= 2);
    setResolvedConfig(null);
    setResolvedEmail(null);
    setPassword('');
    clearMessages();
    setSubView('none');
    tempClientRef.current = null;
    requestAnimationFrame(() => identifierRef.current?.focus());
  }

  const displayName = isStaging
    ? (realZooName ?? (zooNameLoading ? null : 'Setting up your zoo…'))
    : (resolvedConfig?.name ?? zooConfig?.name ?? 'Zoobook');

  const authEmail = resolvedEmail ?? identifier;

  // ─── Auth handlers ───

  function handlePasskeyDiscoverSuccess(result: { access_token: string; refresh_token: string; zoo_config: ZooConfig }) {
    saveZooConfig(result.zoo_config);
    const client = createClient(result.zoo_config.supabaseUrl, result.zoo_config.supabaseAnonKey);
    client.auth.setSession({ access_token: result.access_token, refresh_token: result.refresh_token }).then(() => { window.location.href = '/'; });
  }

  async function handleDiscoverPasskey() {
    clearMessages(); setLoading(true);
    try { handlePasskeyDiscoverSuccess(await discoverWithPasskey()); }
    catch (err) { setError(err instanceof Error ? err.message : 'Passkey authentication failed'); }
    finally { setLoading(false); }
  }

  async function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim()) return;
    clearMessages(); setLoading(true);
    conditionalAbortRef.current?.abort();
    try {
      const config = await resolveIdentifier(identifier.trim().toLowerCase());
      setResolvedConfig(config);
      setResolvedEmail((config as { resolved_email?: string }).resolved_email ?? (identifier.includes('@') ? identifier.trim().toLowerCase() : null));
      setResolved(true);
      setShowAccountChooser(false);
      requestAnimationFrame(() => passwordRef.current?.focus());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zoo not found');
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!resolvedConfig || !authEmail || !password) return;
    clearMessages(); setLoading(true);
    try {
      if (hasZooConfig) {
        if (isOnline) await signIn(authEmail, password);
        else await signInOffline(authEmail, password);
      } else {
        const client = getTempClient(resolvedConfig);
        const { error: e } = await client.auth.signInWithPassword({ email: authEmail, password });
        if (e) throw e;
        saveZooConfig(resolvedConfig);
        refreshZooConfig();
      }
      saveIdentity({ email: authEmail, name: authEmail, avatarUrl: null, zooConfig: resolvedConfig, lastLoginAt: Date.now() });
      if (hasZooConfig) navigate('/', { replace: true });
      else window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskeyLogin() {
    if (!resolvedConfig) return;
    clearMessages(); setLoading(true);
    try {
      const tokens = await startPasskeyLogin(resolvedConfig.supabaseUrl);
      if (hasZooConfig) {
        const client = getSupabase();
        if (!client) throw new Error('Supabase not configured');
        const { error: e } = await client.auth.setSession(tokens);
        if (e) throw e;
        navigate('/', { replace: true });
      } else {
        const client = getTempClient(resolvedConfig);
        const { error: e } = await client.auth.setSession(tokens);
        if (e) throw e;
        saveZooConfig(resolvedConfig);
        window.location.href = '/';
      }
    } catch (err) {
      if (identities.length === 1) { setLoading(false); return; }
      setError(err instanceof Error ? err.message : 'Passkey authentication failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleMagicLink() {
    if (!resolvedConfig || !authEmail) return;
    clearMessages(); setLoading(true);
    try {
      if (hasZooConfig) await sendMagicLink(authEmail);
      else { const c = getTempClient(resolvedConfig); const { error: e } = await c.auth.signInWithOtp({ email: authEmail }); if (e) throw e; saveZooConfig(resolvedConfig); refreshZooConfig(); }
      setSubView('magic-sent');
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send sign-in link');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!resolvedConfig || !authEmail) return;
    clearMessages(); setLoading(true);
    try {
      if (hasZooConfig) await resetPassword(authEmail);
      else { const c = getTempClient(resolvedConfig); await c.auth.resetPasswordForEmail(authEmail); }
    } catch { /* always show generic success */ }
    setSuccess('If an account exists for this email, you will receive a reset link shortly. Check your inbox and spam folder.');
    setResendCooldown(60);
    setLoading(false);
  }

  function selectIdentity(id: StoredIdentity) {
    setResolvedConfig(id.zooConfig);
    setResolvedEmail(id.email);
    setIdentifier(id.email);
    setShowAccountChooser(false);
    setResolved(true);
    requestAnimationFrame(() => passwordRef.current?.focus());
  }

  function handleRemoveIdentity(e: React.MouseEvent, emailToRemove: string) {
    e.stopPropagation();
    const remaining = removeIdentity(emailToRemove);
    if (remaining.length === 0) collapse();
    else if (remaining.length === 1) selectIdentity(remaining[0]);
  }

  // ─── Render: Error/Success banners (animated) ───

  const renderError = () => error ? (
    <div key={error} role="alert" className="auth-error-enter" style={{
      paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
      borderRadius: 12, backgroundColor: 'var(--color-tint-light-red)', color: 'var(--color-tint-red)',
      fontSize: 14, lineHeight: '20px', marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {error}
    </div>
  ) : null;

  const renderSuccess = () => success ? (
    <output key={success} className="auth-fade-enter" style={{
      display: 'flex', alignItems: 'center', gap: 8,
      paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
      borderRadius: 12, backgroundColor: 'var(--color-tint-light-green)', color: 'var(--color-tint-green)',
      fontSize: 14, lineHeight: '20px', marginBottom: 16,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
      </svg>
      {success}
    </output>
  ) : null;

  const renderOffline = () => !isOnline ? (
    <div className="auth-fade-enter" style={{
      paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
      borderRadius: 12, backgroundColor: 'var(--color-fill-warning, #fff3cd)', color: 'var(--color-label-warning, #856404)',
      fontSize: 14, marginBottom: 16, textAlign: 'center',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <line x1="1" y1="1" x2="23" y2="23" /><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" /><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" /><path d="M10.71 5.05A16 16 0 0 1 22.56 9" /><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
      {resolved && policy.offlineLoginEnabled ? 'Offline — sign in with your password' : "You're offline"}
    </div>
  ) : null;

  // ─── Render: Account Chooser (2+ identities) ───

  const renderAccountChooser = () => {
    const stored = getStoredIdentities();
    return (
      <>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {stored[0]?.zooConfig.logoUrl ? <img src={stored[0].zooConfig.logoUrl} alt="Zoo logo" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }} /> : <ZoobookLogo size={64} />}
          <h1 style={{ fontSize: 'var(--font-size-title3)', fontWeight: 700, color: 'var(--color-label-primary)', margin: 0 }}>{stored[0]?.zooConfig.name ?? 'Zoobook'}</h1>
        </div>
        {renderError()}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {stored.map((id) => (
            <button type="button" key={id.email} onClick={() => selectIdentity(id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--color-border-opaque)', backgroundColor: 'var(--color-bg-primary)', cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: 'var(--font-family-primary)', transition: 'background-color 0.1s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-fill-quaternary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)'; }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--color-tint-blue)', color: 'var(--color-label-emphasized)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-footnote)', fontWeight: 600, flexShrink: 0 }}>{getInitials(id.name)}</div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 'var(--font-size-body)', fontWeight: 500, color: 'var(--color-label-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{id.name !== id.email ? id.name : id.email}</div>
                {id.name !== id.email && <div style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{id.email}</div>}
              </div>
              <button type="button" onClick={(ev) => handleRemoveIdentity(ev, id.email)} aria-label={`Remove ${id.name}`} style={{ background: 'none', border: 'none', color: 'var(--color-label-quaternary)', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0, transition: 'color 0.15s ease' }}
                onMouseEnter={(ev) => { ev.currentTarget.style.color = 'var(--color-tint-red)'; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.color = 'var(--color-label-quaternary)'; }}>
                <CloseIcon />
              </button>
            </button>
          ))}
        </div>
        <button type="button" onClick={() => { setShowAccountChooser(false); requestAnimationFrame(() => identifierRef.current?.focus()); }} style={{ ...outlineBtnStyle, marginTop: 12, ...dis(false) }}>+ Someone else</button>
      </>
    );
  };

  // ─── Render: Sub-views (forgot, magic-sent, qr) ───

  if (subView === 'qr') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, backgroundColor: 'var(--color-bg-tertiary)', fontFamily: 'var(--font-family-primary)' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: 32, margin: 16, backgroundColor: 'var(--color-bg-primary)', borderRadius: 16, boxShadow: 'var(--shadow-flat)' }}>
          <QRLoginView onSuccess={handlePasskeyDiscoverSuccess} onBack={() => setSubView('none')} />
        </div>
      </div>
    );
  }

  if (subView === 'forgot') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, backgroundColor: 'var(--color-bg-tertiary)', fontFamily: 'var(--font-family-primary)' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: 32, margin: 16, backgroundColor: 'var(--color-bg-primary)', borderRadius: 16, boxShadow: 'var(--shadow-flat)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {(resolvedConfig ?? zooConfig)?.logoUrl ? <img src={(resolvedConfig ?? zooConfig)!.logoUrl!} alt="Zoo logo" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }} /> : <ZoobookLogo size={64} />}
            <h1 style={{ fontSize: 'var(--font-size-title3)', fontWeight: 700, color: 'var(--color-label-primary)', margin: 0 }}>Reset your password</h1>
          </div>
          {renderError()}{renderSuccess()}
          <output style={{ display: 'block', padding: '10px 14px', borderRadius: 8, backgroundColor: 'var(--color-fill-quaternary)', fontSize: 'var(--font-size-body)', color: 'var(--color-label-primary)', fontWeight: 500, fontFamily: 'var(--font-family-primary)', marginBottom: 16 }}>{authEmail}</output>
          {!success && <button type="button" onClick={handleForgotPassword} disabled={loading} style={{ ...primaryBtnStyle, ...dis(loading) }}>{loading ? 'Sending…' : 'Send reset link'}</button>}
          {!success && passkeyAvailable && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0', color: 'var(--color-label-tertiary)', fontSize: 'var(--font-size-caption)' }}><div style={{ flex: 1, height: 1, backgroundColor: 'var(--color-border-opaque)' }} />or<div style={{ flex: 1, height: 1, backgroundColor: 'var(--color-border-opaque)' }} /></div>
              <button type="button" onClick={handlePasskeyLogin} disabled={loading} style={{ ...outlineBtnStyle, ...dis(loading) }}><BiometricIcon /> Set up {getPasskeyLabel().replace('Sign in with ', '')} instead</button>
            </>
          )}
          {success && resendCooldown > 0 && <p style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)', textAlign: 'center', margin: '12px 0 0' }}>Resend available in {resendCooldown}s</p>}
          {success && resendCooldown <= 0 && <button type="button" onClick={handleForgotPassword} disabled={loading} style={{ ...textBtnStyle, justifyContent: 'center', width: '100%', marginTop: 8 }}>Resend</button>}
          <button type="button" onClick={() => { setSubView('none'); clearMessages(); }} style={{ ...textBtnStyle, color: 'var(--color-label-tertiary)', marginTop: 16 }}>← Back to sign in</button>
        </div>
      </div>
    );
  }

  if (subView === 'magic-sent') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, backgroundColor: 'var(--color-bg-tertiary)', fontFamily: 'var(--font-family-primary)' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: 32, margin: 16, backgroundColor: 'var(--color-bg-primary)', borderRadius: 16, boxShadow: 'var(--shadow-flat)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {(resolvedConfig ?? zooConfig)?.logoUrl ? <img src={(resolvedConfig ?? zooConfig)!.logoUrl!} alt="Zoo logo" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }} /> : <ZoobookLogo size={64} />}
            <h1 style={{ fontSize: 'var(--font-size-title3)', fontWeight: 700, color: 'var(--color-label-primary)', margin: 0 }}>Check your email</h1>
          </div>
          <p style={{ fontSize: 'var(--font-size-body)', color: 'var(--color-label-secondary)', textAlign: 'center', margin: '0 0 4px' }}>We sent a sign-in link to</p>
          <p style={{ fontSize: 'var(--font-size-body)', fontWeight: 600, color: 'var(--color-label-primary)', textAlign: 'center', margin: '0 0 16px' }}>{authEmail}</p>
          <p style={{ fontSize: 'var(--font-size-footnote)', color: 'var(--color-label-tertiary)', textAlign: 'center', margin: '0 0 20px' }}>Click the link in your email to sign in. No password needed.</p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            {resendCooldown > 0 ? <span style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)' }}>Resend available in {resendCooldown}s</span> : <button type="button" onClick={handleMagicLink} disabled={loading} style={{ ...textBtnStyle, justifyContent: 'center' }}>Resend</button>}
            <button type="button" onClick={() => { setSubView('none'); clearMessages(); }} style={{ ...textBtnStyle, justifyContent: 'center', color: 'var(--color-label-tertiary)' }}>Use password instead</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Main card (progressive expand) ───

  const config = resolvedConfig ?? zooConfig;
  const showBiometric = passkeyAvailable && isOnline;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, backgroundColor: 'var(--color-bg-tertiary)', fontFamily: 'var(--font-family-primary)', padding: 'env(safe-area-inset-top, 0) env(safe-area-inset-right, 0) env(safe-area-inset-bottom, 0) env(safe-area-inset-left, 0)' }}>
      <style>{AUTH_ANIMATIONS_CSS}</style>

      {showAccountChooser ? (
        <div style={{ width: '100%', maxWidth: 420, padding: '40px 36px', margin: 16, backgroundColor: 'var(--color-bg-primary)', borderRadius: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}>
          {renderAccountChooser()}
        </div>
      ) : (
        <div style={{ width: '100%', maxWidth: 420, padding: '40px 36px', margin: 16, backgroundColor: 'var(--color-bg-primary)', borderRadius: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}>

          {/* Branding — crossfade between Zoobook and zoo logo/name */}
          <div style={{ textAlign: 'center', marginBottom: resolved ? 28 : 36, transition: 'margin-bottom 0.25s ease' }}>
            {resolved && config?.logoUrl ? (
              <img src={config.logoUrl} alt={`${displayName} logo`} style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', marginBottom: 14, transition: 'all 0.25s ease' }} />
            ) : (
              <ZoobookLogo size={resolved ? 56 : 64} />
            )}
            <h1 style={{ fontSize: resolved ? 22 : 28, lineHeight: resolved ? '28px' : '34px', fontWeight: 700, color: 'var(--color-label-primary)', margin: 0, transition: 'font-size 0.25s ease', letterSpacing: '-0.02em' }}>
              {resolved ? (displayName ?? 'Zoobook') : 'Zoobook'}
            </h1>
            {!resolved && <p style={{ fontSize: 16, lineHeight: '22px', color: 'var(--color-label-tertiary)', margin: '6px 0 0', letterSpacing: '-0.01em' }}>Connect to your zoo</p>}
          </div>

          {renderOffline()}
          {renderError()}
          {renderSuccess()}

          {/*
            Single form — additive progressive disclosure.
            Nothing disappears. Password slides in, button changes text.
          */}
          <form onSubmit={resolved ? handlePasswordLogin : handleContinue}>

            {/* Identifier: input morphs to pill in-place */}
            {!resolved ? (
              <>
                <label htmlFor="auth-identifier" style={labelStyle}>Email or employee ID</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="auth-identifier"
                    ref={identifierRef}
                    type="text"
                    placeholder="you@zoo-liberec.cz"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    autoFocus={!passkeyAvailable}
                    autoComplete="username webauthn"
                    enterKeyHint="next"
                    style={{ ...inputStyle, paddingRight: showBiometric ? 48 : 16 }}
                    onFocus={focusBorder}
                    onBlur={blurBorder}
                  />
                  {showBiometric && (
                    <button type="button" onClick={handleDiscoverPasskey} disabled={loading} aria-label={getPasskeyLabel()} style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--color-tint-blue)', cursor: loading ? 'not-allowed' : 'pointer', paddingTop: 10, paddingBottom: 10, paddingLeft: 10, paddingRight: 10, display: 'flex', borderRadius: 8 }}>
                      <BiometricIcon />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Apple grouped input: one bordered box, email row (tinted bg) + password row */}
              <div style={{ border: '1.5px solid var(--color-border-opaque)', borderRadius: 12, overflow: 'hidden' }}>
                {/* Email row — subtle background to distinguish from password */}
                <div style={{ display: 'flex', alignItems: 'center', paddingTop: 13, paddingBottom: 13, paddingLeft: 16, paddingRight: 12, gap: 8, backgroundColor: 'var(--color-fill-quaternary)' }}>
                  <span style={{ flex: 1, fontSize: 16, color: 'var(--color-label-primary)', fontWeight: 500, fontFamily: 'var(--font-family-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{authEmail}</span>
                  {showBiometric && (
                    <button type="button" onClick={handlePasskeyLogin} disabled={loading} aria-label={getPasskeyLabel()} style={{ background: 'none', border: 'none', color: 'var(--color-tint-blue)', cursor: loading ? 'not-allowed' : 'pointer', paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, display: 'flex', flexShrink: 0 }}>
                      <BiometricIcon size={18} />
                    </button>
                  )}
                </div>
                {/* Divider line */}
                <div style={{ height: 1, backgroundColor: 'var(--color-border-opaque)' }} />
                {/* Password row */}
                <input
                  id="auth-password"
                  ref={passwordRef}
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  enterKeyHint="send"
                  style={{ width: '100%', paddingTop: 13, paddingBottom: 13, paddingLeft: 16, paddingRight: 16, border: 'none', outline: 'none', fontSize: 16, lineHeight: '24px', color: 'var(--color-label-primary)', fontFamily: 'var(--font-family-primary)', backgroundColor: 'var(--color-bg-primary)', boxSizing: 'border-box' as const, minHeight: 50 }}
                />
              </div>
              {/* Actions under the grouped input */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <button type="button" onClick={collapse} style={{ background: 'none', border: 'none', color: 'var(--color-label-tertiary)', fontSize: 14, fontFamily: 'var(--font-family-primary)', fontWeight: 500, cursor: 'pointer', paddingTop: 4, paddingBottom: 4, paddingLeft: 0, paddingRight: 0, minHeight: 32, transition: 'color 0.15s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-label-secondary)'; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-label-tertiary)'; }}>Not you?</button>
                {isOnline && (
                  <button type="button" onClick={() => { setSubView('forgot'); clearMessages(); }} style={{ background: 'none', border: 'none', color: 'var(--color-tint-blue)', fontSize: 14, fontFamily: 'var(--font-family-primary)', fontWeight: 500, cursor: 'pointer', paddingTop: 4, paddingBottom: 4, paddingLeft: 0, paddingRight: 0, minHeight: 32 }}>Forgot password?</button>
                )}
              </div>
              </>
            )}

            {/* Single button — changes text, never disappears */}
            <button
              type="submit"
              disabled={resolved ? (loading || !password) : (loading || !identifier.trim() || !isOnline)}
              style={{
                ...primaryBtnStyle,
                ...dis(resolved ? (loading || !password) : (loading || !identifier.trim() || !isOnline)),
                marginTop: 12,
              }}
            >
              {loading
                ? (resolved ? 'Signing in…' : 'Searching…')
                : (resolved ? 'Sign in' : 'Continue')}
            </button>
          </form>

          {/* Magic link — fades in below button when resolved */}
          <div style={expandStyle(resolved && isOnline && !!authEmail)}>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={handleMagicLink} disabled={loading} style={{ ...textBtnStyle, justifyContent: 'center', width: '100%', color: 'var(--color-label-tertiary)' }}>Send sign-in link instead</button>
            </div>
          </div>

          {/* QR section — always visible, never disappears */}
          {isOnline && (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, marginBottom: 16, color: 'var(--color-label-tertiary)', fontSize: 13 }}>
                <div style={{ flex: 1, height: 1, backgroundColor: 'var(--color-border-opaque)' }} />or<div style={{ flex: 1, height: 1, backgroundColor: 'var(--color-border-opaque)' }} />
              </div>
              <button type="button" onClick={() => setSubView('qr')} disabled={loading} style={{ ...outlineBtnStyle, ...dis(loading) }}><QrIcon /> Scan QR from mobile</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
